/**
 * Service for handling château filtering and recommendation logic
 */

export interface FilterCriteria {
  space: string;
  budget: string;
}

export interface BudgetRange {
  min: number;
  max: number;
}

export class ChateauFilterService {
  private static readonly BUDGET_RANGES: Record<string, BudgetRange> = {
    "30-60€": { min: 30, max: 60 },
    "60-90€": { min: 30, max: 90 }, // Inclut la catégorie 30-60€
    "90-120€": { min: 30, max: 120 }, // Inclut toutes les catégories inférieures
    "tous": { min: 0, max: 999 } // Tous les budgets
  };

  /**
   * Extract dimensions from size string handling French decimal notation
   */
  private static extractDimensions(taille: string): { width: number; length: number } | null {
    const cleanTaille = taille.replace(/,/g, '.');
    const dimensions = cleanTaille.match(/(\d+(?:\.\d+)?)/g);
    
    if (dimensions && dimensions.length >= 2) {
      const width = parseFloat(dimensions[0]);
      const length = parseFloat(dimensions[1]);
      return { width, length };
    }
    
    return null;
  }

  /**
   * Check if château fits space criteria
   */
  private static matchesSpaceCriteria(chateau: any, spaceCriteria: string): boolean {
    if (spaceCriteria === "Petit (largeur max de 4m)") {
      const dimensions = this.extractDimensions(chateau.taille);
      if (dimensions) {
        const maxDimension = Math.max(dimensions.width, dimensions.length);
        return maxDimension <= 4;
      }
      return false;
    }
    
    // For medium and large spaces, all châteaux are acceptable
    return true;
  }

  /**
   * Check if château fits budget criteria
   */
  private static matchesBudgetCriteria(chateau: any, budgetCriteria: string): boolean {
    const budgetRange = this.BUDGET_RANGES[budgetCriteria];
    if (!budgetRange) return false;

    const prix = parseInt(chateau.prix.replace(/[^\d]/g, ''));
    return prix >= budgetRange.min && prix <= budgetRange.max;
  }

  /**
   * Filter châteaux based on criteria
   */
  static filterChateaux(chateaux: any[], criteria: FilterCriteria): any[] {
    if (!criteria.space || !criteria.budget || !chateaux.length) {
      return [];
    }

    return chateaux.filter(chateau => {
      const matchesSpace = this.matchesSpaceCriteria(chateau, criteria.space);
      const matchesBudget = this.matchesBudgetCriteria(chateau, criteria.budget);
      
      return matchesSpace && matchesBudget;
    });
  }

  /**
   * Get space options for dropdown
   */
  static getSpaceOptions(): Array<{ value: string; label: string }> {
    return [
      { value: "", label: "Sélectionnez votre espace" },
      { value: "Petit (largeur max de 4m)", label: "Petit (largeur max de 4m)" },
      { value: "Moyen (max 6m x 6m)", label: "Moyen (max 6m x 6m)" },
      { value: "Grand (plus de 6m x 6m)", label: "Grand (plus de 6m x 6m)" }
    ];
  }

  /**
   * Get budget options for dropdown
   */
  static getBudgetOptions(): Array<{ value: string; label: string }> {
    return [
      { value: "", label: "Sélectionnez votre budget" },
      { value: "30-60€", label: "30-60€" },
      { value: "60-90€", label: "60-90€ (inclut 30-60€)" },
      { value: "90-120€", label: "90-120€ (inclut tous)" },
      { value: "tous", label: "Tous budgets" }
    ];
  }
}